import type { NetworkDashboardAvailableStore } from '@gozoki/api-types'
import {
  comparisonShiftDays,
  networkComparisonPeriod,
  type NetworkComparisonMode,
  SAME_WEEKDAYS_MAX_WINDOW_DAYS,
} from '@gozoki/api-types/src/network-comparison'
import { z } from 'zod'
import type { NetworkDashboardDepartmentGroup } from '~/components/dashboards/filters/group-stores-by-department'
import { pluralize } from '~/lib/utils'
import {
  addDays,
  type DateRange,
  parseDay,
  rangeDays,
  rangeText,
  toIso,
  yearOf,
} from './date-range-model'
import { recentsStore } from './filter-recents'
import type { PanelLabel } from './period-model'
import { coveringDepartments, departmentName, touchedDepartmentCount } from './store-selector-model'

type Store = NetworkDashboardAvailableStore

export const COMPARISON_PRESETS = [
  'previous',
  'month_before',
  'four_weeks_before',
  'year_before',
] as const
export type ComparisonPreset = (typeof COMPARISON_PRESETS)[number]

export const COMPARISON_PERIODS = [...COMPARISON_PRESETS, 'custom'] as const
export type ComparisonPeriodChoice = (typeof COMPARISON_PERIODS)[number]

export const COMPARED_STORE_SETS = ['same', 'network', 'rest', 'custom'] as const
export type ComparedStoreSet = (typeof COMPARED_STORE_SETS)[number]

export interface Comparison {
  enabled: boolean
  period: ComparisonPeriodChoice
  customRange: DateRange | null
  stores: ComparedStoreSet
  storeIds: number[]
}

export const DEFAULT_COMPARISON: Comparison = {
  enabled: true,
  period: 'previous',
  customRange: null,
  stores: 'same',
  storeIds: [],
}

export interface ComparedPeriods {
  periodStart: string
  periodEnd: string
  compStart: string
  compEnd: string
}

export interface ComparedStoresContext {
  stores: Store[]
  groups: NetworkDashboardDepartmentGroup[]
  includedIds: number[]
}

const monthsBefore = ({ startDate, endDate }: DateRange, months: number): DateRange => ({
  startDate: toIso(parseDay(startDate).minus({ months })),
  endDate: toIso(parseDay(endDate).minus({ months })),
})

export function comparisonPresetRange(period: DateRange, preset: ComparisonPreset): DateRange {
  if (preset === 'month_before') return monthsBefore(period, 1)
  if (preset === 'four_weeks_before') {
    return { startDate: addDays(period.startDate, -28), endDate: addDays(period.endDate, -28) }
  }
  const { compStart, compEnd } = networkComparisonPeriod(
    period.startDate,
    period.endDate,
    comparisonApiMode(preset),
  )
  return { startDate: compStart, endDate: compEnd }
}

export const comparisonRange = (
  period: DateRange,
  { period: choice, customRange }: Pick<Comparison, 'period' | 'customRange'>,
): DateRange =>
  choice === 'custom' && customRange !== null
    ? customRange
    : comparisonPresetRange(period, choice === 'custom' ? 'previous' : choice)

export const comparisonApiMode = (choice: ComparisonPeriodChoice): NetworkComparisonMode =>
  choice === 'year_before' ? 'last_year' : 'previous'

export function comparisonPresetLabel(preset: ComparisonPreset, period: DateRange): string {
  if (preset === 'month_before') return '1 mois avant'
  if (preset === 'four_weeks_before') return '4 semaines avant'
  if (preset === 'year_before') return '1 an avant'
  return `${comparisonShiftDays(rangeDays(period))} jours avant`
}

export const comparedRangeText = (range: DateRange, periodEnd: string): string =>
  rangeText(range, { year: yearOf(periodEnd) })

export function comparisonPeriodSummary(
  choice: ComparisonPeriodChoice,
  period: DateRange,
  range: DateRange | null,
): PanelLabel {
  if (range === null) return { title: 'Plage invalide', muted: true }
  return {
    title: comparedRangeText(range, period.endDate),
    detail: choice === 'custom' ? 'Plage libre' : comparisonPresetLabel(choice, period),
  }
}

export function comparedStoreIds(
  set: ComparedStoreSet,
  storeIds: number[],
  { stores, includedIds }: Pick<ComparedStoresContext, 'stores' | 'includedIds'>,
): number[] {
  if (set === 'same') return includedIds
  if (set === 'custom') return storeIds
  const included = new Set(includedIds)
  return stores
    .filter((store) => set === 'network' || !included.has(store.id))
    .map((store) => store.id)
}

function groupName(ids: number[], { stores, groups }: ComparedStoresContext): string | null {
  const set = new Set(ids)
  const covering = coveringDepartments(groups, set)
  if (covering) return covering.map(departmentName).join(', ')
  if (ids.length === 1) return stores.find((store) => store.id === ids[0])?.label ?? null
  return null
}

export function comparedStoresSuffix(
  set: ComparedStoreSet,
  ids: number[],
  context: ComparedStoresContext,
): string | null {
  if (set === 'same') return null
  if (set === 'network') return 'tout le réseau'
  if (set === 'rest') return 'reste du réseau'
  return groupName(ids, context) ?? pluralize(ids.length, 'boutique')
}

export const COMPARED_STORE_SET_LABELS: Record<Exclude<ComparedStoreSet, 'custom'>, string> = {
  same: 'Les mêmes boutiques',
  network: 'Tout le réseau',
  rest: 'Le reste du réseau',
}

export function comparedStoresValue(
  set: ComparedStoreSet,
  ids: number[],
  context: ComparedStoresContext,
): string {
  const count = pluralize(ids.length, 'boutique')
  if (set === 'same') return count
  if (set !== 'custom') return `${COMPARED_STORE_SET_LABELS[set]} - ${count}`
  const covering = coveringDepartments(context.groups, new Set(ids))
  if (covering) return `${covering.map((group) => group.label).join(', ')} - ${count}`
  return groupName(ids, context) ?? count
}

export function comparedStoresSummary(
  set: ComparedStoreSet,
  ids: number[],
  context: ComparedStoresContext,
): PanelLabel {
  const count = pluralize(ids.length, 'boutique')
  const name = set === 'custom' ? groupName(ids, context) : COMPARED_STORE_SET_LABELS[set]
  return {
    title: name ? `${name} - ${count}` : count,
    detail: pluralize(touchedDepartmentCount(context.groups, new Set(ids)), 'département'),
  }
}

function periodTag(period: DateRange, choice: ComparisonPeriodChoice, range: DateRange): string {
  const days = rangeDays(period)
  if (
    choice === 'previous' &&
    rangeDays(range) === days &&
    addDays(range.endDate, 1) === period.startDate
  ) {
    return `${days} j. précédents`
  }
  return comparedRangeText(range, period.endDate)
}

const withSuffix = (label: string, suffix: string | null) =>
  suffix === null ? label : `${label} - ${suffix}`

export function comparisonLabel(
  comparison: Comparison,
  period: DateRange,
  context: ComparedStoresContext,
): string {
  const ids = comparedStoreIds(comparison.stores, comparison.storeIds, context)
  return withSuffix(
    `vs ${periodTag(period, comparison.period, comparisonRange(period, comparison))}`,
    comparedStoresSuffix(comparison.stores, ids, context),
  )
}

const PERIOD_UNIT_BY_DAYS: Record<number, string> = { 1: 'J', 7: 'S', 30: 'M', 365: 'A' }

export const periodUnit = (range: DateRange): string => PERIOD_UNIT_BY_DAYS[rangeDays(range)] ?? 'C'

function comparedUnit(period: DateRange, compared: DateRange): string {
  const sameWeekdaysOfPreviousWeek =
    rangeDays(period) <= SAME_WEEKDAYS_MAX_WINDOW_DAYS &&
    addDays(compared.startDate, 7) === period.startDate
  return sameWeekdaysOfPreviousWeek ? 'S' : periodUnit(compared)
}

export function comparisonTag(comparison: Comparison, period: DateRange): string {
  if (comparison.period === 'year_before') return 'N-1'
  if (comparison.period === 'month_before') return 'M-1'
  if (comparison.period === 'four_weeks_before') return 'S-4'
  const unit = comparedUnit(period, comparisonRange(period, comparison))
  return comparison.period === 'custom' || unit === 'C' ? unit : `${unit}-1`
}

export function comparisonRecentRow({
  period,
  range,
  suffix,
  storeCount,
}: {
  period: DateRange
  range: DateRange
  suffix: string | null
  storeCount: number
}): { title: string; meta: string } {
  return {
    title: withSuffix(`vs ${comparedRangeText(range, period.endDate)}`, suffix),
    meta: `${pluralize(rangeDays(range), 'jour')} - ${pluralize(storeCount, 'boutique')}`,
  }
}

export function comparisonBoundsLabel(bounds: ComparedPeriods | null): string {
  if (bounds === null) return ''
  const period = { startDate: bounds.periodStart, endDate: bounds.periodEnd }
  const compared = { startDate: bounds.compStart, endDate: bounds.compEnd }
  const preset = COMPARISON_PRESETS.find((choice) => {
    const range = comparisonPresetRange(period, choice)
    return range.startDate === compared.startDate && range.endDate === compared.endDate
  })
  const comparison: Comparison = {
    ...DEFAULT_COMPARISON,
    period: preset ?? 'custom',
    customRange: compared,
  }
  return `vs ${comparisonTag(comparison, period)}`
}

const recentSchema = z.object({
  period: z.enum(COMPARISON_PERIODS),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  stores: z.enum(COMPARED_STORE_SETS),
  storeIds: z.array(z.number()),
  at: z.string(),
})

export type ComparisonRecent = z.infer<typeof recentSchema>

export const comparisonRecents = recentsStore<ComparisonRecent>({
  key: 'gozoki.network-dashboard.comparison-picker.recents.v1',
  schema: recentSchema,
  identity: (entry) =>
    [entry.period, entry.startDate, entry.endDate, entry.stores, entry.storeIds.join(',')].join(
      '|',
    ),
})

export const comparisonRecentOf = (comparison: Comparison, at: string): ComparisonRecent => ({
  period: comparison.period,
  ...(comparison.period === 'custom' && comparison.customRange ? comparison.customRange : {}),
  stores: comparison.stores,
  storeIds: comparison.stores === 'custom' ? comparison.storeIds : [],
  at,
})

export const comparisonOfRecent = (recent: ComparisonRecent): Comparison => ({
  enabled: true,
  period: recent.period,
  customRange:
    recent.period === 'custom' && recent.startDate && recent.endDate
      ? { startDate: recent.startDate, endDate: recent.endDate }
      : null,
  stores: recent.stores,
  storeIds: recent.storeIds,
})
